import { inject, Injectable } from '@angular/core'
import { ConfigService } from './config.service'
import { catchError, map } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import { ConfigKeys } from '../features/config/ConfigKeys'

export type TargetControl = string
export type DataSourceId = string
export type ColumnLayout = DataSourceId[]

// Maps a target control to its respective configuration key
const controlToConfigKey = {
  assistantList: ConfigKeys.ASSISTANT_LIST_CUSTOM_COLUMNS,
}

@Injectable({
  providedIn: 'root',
})
export class ColumnConfigService {
  protected configService = inject(ConfigService)

  /**
   * Gets configured column layout for the specified control.
   * @param control
   * @returns {Observable<string[]>}
   */
  getForControl(control: TargetControl): Observable<ColumnLayout> {
    return this.configService.getByKey(controlToConfigKey[control]).pipe(
      map((config) => {
        if (!config || !config.value) {
          console.warn(`No config found for control: ${control}`)
          return []
        }
        return ColumnConfigService.parseConfigValue(config.value)
      }),
      catchError((error) => {
        console.error(`Error getting column layout for control ${control}:`, error)
        // Return empty array as fallback to prevent component from breaking
        return of([])
      }),
    )
  }

  /**
   * Updates configured column layout for the specified control.
   * @param control
   * @param newColumnLayout
   * @return {Promise<Config[] | Config | C>}
   */
  async updateForControl(control: TargetControl, newColumnLayout: ColumnLayout) {
    try {
      const valueStr = ColumnConfigService.buildConfigValue(newColumnLayout)
      return await this.configService.updateByKey(controlToConfigKey[control], valueStr)
    } catch (error) {
      console.error(`Error updating column layout for control ${control}:`, error)
      throw error
    }
  }

  /**
   * Builds a string configuration value from an object.
   * @param columnLayout
   * @returns {string}
   */
  static buildConfigValue(columnLayout: ColumnLayout) {
    return columnLayout.join(',')
  }

  /**
   * Parses an object into a string configuration value.
   * @param value
   * @returns {string[]}
   */
  static parseConfigValue(value: string) {
    try {
      const str = (value ?? '').trim()
      return str === '' ? [] : str.split(/\s*,\s*/)
    } catch (error) {
      console.error('Error parsing config value:', error, 'Value:', value)
      return []
    }
  }
}
